import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import { isEmpty, isNil, pickBy, sumBy } from "lodash";
import { type TimeFrameFilter } from "@/components/OrderSearchBar/OrderSearchBar.tsx";
import { createApiClient } from "@/config/createApiClient";
import { trackHeapEvent } from "@/helpers/heap";
import { selectedClientServiceIdAtom } from "@/store";
import {
  OrderApi,
  type OrderDuplicateOrderCommandDuplicateFilesOptionEnum,
  type OrdersControllerGetOrdersFilterEnum,
  type OrderValidateCommand,
} from "../../config/apiClient";
import { useWithTrackingMutation } from "./useWithTrackingMutation";

interface QueryGetOrders {
  currentPage: number;
  filter?: {
    status?: OrdersControllerGetOrdersFilterEnum;
    assignation?: "all" | "assignedOnly";
  };
  timeFrameFilter?: TimeFrameFilter;
  search?: string;
  orderBy?: "createdAt" | "status";
  nbPerPage?: number;
  asRoleClient?: boolean;
}

const defaultTimeFrameFilter: TimeFrameFilter = { title: "default" };

const apiClient = createApiClient(OrderApi);

export const useOrders = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useCreateOrder = () => {
    return useMutation({
      mutationKey: ["createOrder"],
      mutationFn: ({
        orderName,
        assignedClientsIds,
      }: {
        orderName: string;
        assignedClientsIds?: string[];
      }) =>
        apiClient.ordersControllerCreateOrder({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderCreateCommand: {
            orderName: orderName,
            assignedClientsIds: assignedClientsIds,
          },
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });
  };

  const useValidateOrder = () =>
    useWithTrackingMutation<
      { orderId: string; orderValidateCommand: OrderValidateCommand },
      Awaited<ReturnType<typeof apiClient.ordersControllerValidateOrder>>
    >(
      (params) =>
        apiClient.ordersControllerValidateOrder({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderValidateCommand: params.orderValidateCommand,
          orderId: params.orderId,
        }),
      [
        {
          name: "validate_order",
          getMetaData: (res) => ({
            order_id: res?.data?.id,
            reference_id: res?.data?.referenceId ?? null,
          }),
        },
        {
          name: "multi_product_order",
          enable: (res) => res.data.OrderItem.length > 1,
          getMetaData: (res) => ({
            order_id: res.data.id,
            product_count: res.data.OrderItem.length,
          }),
        },
        {
          name: "single_product_order",
          enable: (res) => res.data.OrderItem.length === 1,
          getMetaData: (res) => ({
            order_id: res.data.id,
          }),
        },
        {
          name: "delivery_type_selected",
          sendEvent: (res) => {
            for (const orderItem of res.data.OrderItem) {
              trackHeapEvent("delivery_type_selected", {
                order_id: res.data.id,
                order_item_id: orderItem.id,
                delivery_type: orderItem.sandboxItem.dispatchType,
              });
            }
          },
        },
        {
          name: "add_delivery_address",
          sendEvent: (res) => {
            for (const orderItem of res.data.OrderItem) {
              if (orderItem.sandboxItem.dispatchType !== "manualDispatch") {
                continue;
              }
              trackHeapEvent("add_delivery_address", {
                order_id: res.data.id,
                order_item_id: orderItem.id,
                product_id: orderItem.sandboxItem.catalogMediumId ?? null,
                address_count: sumBy(
                  orderItem.sandboxItem.SandboxItemVersion,
                  (sv) => {
                    return sv.SandboxItemVersionDispatch.length;
                  }
                ),
              });
            }
          },
        },
      ],
      {
        mutationKey: ["validateOrder"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          ]),
      }
    );

  const useGetOrders = ({
    currentPage = 0,
    filter = {
      status: "all",
      assignation: "assignedOnly",
    },
    timeFrameFilter = defaultTimeFrameFilter,
    search = "",
    orderBy = "createdAt",
    nbPerPage = 10,
    asRoleClient = false,
  }: QueryGetOrders) =>
    useQuery({
      queryKey: [
        "orders",
        orderBy,
        timeFrameFilter,
        currentPage,
        filter,
        search,
        nbPerPage,
        selectedClientServiceIdAtomValue,
        asRoleClient,
      ],
      enabled: selectedClientServiceIdAtomValue > 0,
      queryFn: () => {
        const queryParams: {
          page: string;
          filter?: string;
          search?: string;
          startDate?: string;
          stopDate?: string;
          orderBy?: "createdAt" | "status";
          nbPerPage?: string;
        } = {
          page: String(currentPage),
          filter: JSON.stringify({
            status: filter.status ?? "all",
            assignation: filter.assignation ?? "assignedOnly",
          }),
          orderBy,
          search: search || "",
          nbPerPage: String(nbPerPage),
        };

        queryParams.startDate =
          timeFrameFilter?.startDate !== undefined
            ? dayjs(timeFrameFilter?.startDate).toISOString()
            : undefined;
        queryParams.stopDate =
          timeFrameFilter?.stopDate !== undefined
            ? dayjs(timeFrameFilter?.stopDate).toISOString()
            : undefined;

        const cleanQueryParams = pickBy(
          queryParams,
          (param) => !isNil(param) && !isEmpty(param)
        );

        return apiClient.ordersControllerGetOrders({
          clientServiceId: selectedClientServiceIdAtomValue,
          ...cleanQueryParams,
          ...(asRoleClient ? { asRoleClient } : {}),
        });
      },
    });

  const useAddPurchaseOrder = () =>
    useWithTrackingMutation<
      {
        orderId: string;
        orderAddPurchaseOrderCommand: Parameters<
          typeof apiClient.ordersControllerAddPurchaseOrder
        >[0]["orderAddPurchaseOrderCommand"];
      },
      Awaited<ReturnType<typeof apiClient.ordersControllerAddPurchaseOrder>>
    >(
      (body) =>
        apiClient.ordersControllerAddPurchaseOrder({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: body.orderId,
          orderAddPurchaseOrderCommand: body.orderAddPurchaseOrderCommand,
        }),
      [
        {
          name: "add_purchase_order",
          getMetaData: (res) => ({
            creation_time: res.data.createdAt,
            order_id: res.data.OrderItem[0]?.orderId,
          }),
        },
      ],
      {
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId],
            }),
            queryClient.invalidateQueries({
              queryKey: ["orderItemLogs", params.orderId],
            }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          ]),
      }
    );

  const useSyncWithAws = () => {
    return useMutation({
      mutationFn: (params: { orderId: string }) =>
        apiClient.ordersControllerAwsSync({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
        ]),
    });
  };

  const useGetOrder = ({ orderId }: { orderId?: string }) =>
    useQuery({
      enabled: selectedClientServiceIdAtomValue > 0 && !!orderId,
      queryKey: ["order", orderId, selectedClientServiceIdAtomValue],
      queryFn: () =>
        apiClient.ordersControllerGetOrder({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: orderId as string,
        }),
    });

  const useGetOrderDocuments = ({ orderId }: { orderId?: string }) =>
    useMutation({
      mutationFn: () =>
        apiClient.ordersControllerDownloadAllOrderDocuments({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: orderId as string,
        }),
    });

  const useAssignUsers = () => {
    return useMutation({
      mutationKey: ["assignuser"],
      mutationFn: ({
        orderId,
        usersIds,
      }: {
        orderId: string;
        usersIds: string[];
      }) =>
        apiClient.ordersControllerAssignUsersToOrder({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId,
          orderUsersAssignCommand: { usersIds },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItem", params.orderId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId],
          }),
          queryClient.invalidateQueries({ queryKey: ["getUsers"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });
  };

  const useGetActiveOrders = () =>
    useQuery({
      enabled: selectedClientServiceIdAtomValue > 0,
      queryKey: ["activeOrders", selectedClientServiceIdAtomValue],
      queryFn: () => {
        return apiClient.ordersControllerGetOrders({
          clientServiceId: selectedClientServiceIdAtomValue,
          filter: "active",
          page: "0",
          nbPerPage: 999,
        });
      },
    });

  const useDeleteOrder = (orderId: string) =>
    useMutation({
      mutationKey: ["deleteOrder"],
      mutationFn: () =>
        apiClient.ordersControllerDeleteOrder({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId,
        }),

      onSuccess: async () => {
        void queryClient.removeQueries({ queryKey: ["order", orderId] });
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["orders"] }),
          queryClient.invalidateQueries({ queryKey: ["orderItem", orderId] }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]);
      },
    });

  const useDuplicateOrder = () =>
    useMutation({
      mutationKey: ["duplicateOrder"],
      mutationFn: ({
        orderId,
        orderName,
        duplicateFilesOption,
      }: {
        orderId: string;
        orderName: string;
        duplicateFilesOption?: OrderDuplicateOrderCommandDuplicateFilesOptionEnum;
      }) =>
        apiClient.ordersControllerDuplicateOrder({
          orderId: orderId,
          clientServiceId: selectedClientServiceIdAtomValue,
          orderDuplicateOrderCommand: {
            orderName: orderName,
            duplicateFilesOption: duplicateFilesOption,
          },
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useRushOrder = () => {
    return useMutation({
      mutationFn: ({
        orderId,
        orderItemId,
        newDeliveryDate,
        isValidatedByClient,
      }: {
        newDeliveryDate?: string;
        isValidatedByClient?: boolean;
        orderId: string;
        orderItemId: string;
      }) =>
        apiClient.ordersControllerHandleRushOrder({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: orderId,
          orderItemId: orderItemId,
          orderRushOrderCommand: {
            newDeliveryDate,
            ...(isValidatedByClient && {
              isValidatedByClient: isValidatedByClient,
            }),
          },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
        ]),
    });
  };

  const useOrderNameAvailability = () =>
    useMutation({
      mutationFn: (params: {
        orderName: string;
        orderIdToExclude?: string;
      }) => {
        const queryParams: {
          orderIdToExclude?: string;
        } = {
          orderIdToExclude: params.orderIdToExclude,
        };

        const cleanQueryParams = pickBy(
          queryParams,
          (param) => !isNil(param) && !isEmpty(param)
        );

        return apiClient.ordersControllerCheckOrderNameAvailability({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderName: params.orderName,
          ...cleanQueryParams,
        });
      },
    });

  return {
    useValidateOrder,
    useGetOrders,
    useGetActiveOrders,
    useGetOrder,
    useGetOrderDocuments,
    useAddPurchaseOrder,
    useSyncWithAws,
    useAssignUsers,
    useDeleteOrder,
    useDuplicateOrder,
    useRushOrder,
    useCreateOrder,
    useOrderNameAvailability,
  };
};
